import { useCallback, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { MODAL_TEMPLATE_MESSAGE } from '@/components/organisms/Modals/ModalConductor';
import { useDeskId } from '@/hooks/useDeskId';
import useMessages from '@/hooks/useMessages';
import { useConversationDetail } from '@/modules/humanChat/hooks/useConversations';
import { popModal, pushModal } from '@/redux/modals/actions';
import { selectSessionId } from '@/redux/session/selectors';
import { getDaysDifference } from '@/util/dateUtils';

// 'send' fires the template immediately; 'schedule' opens the send-time wizard.
export type TemplateMessageMode = 'send' | 'schedule';

/**
 * Determines when to show template message UI for WhatsApp conversations.
 * Shows template option when conversation is active AND either:
 * - Last system message (agent/brain/broadcast/campaign) failed with error 131047 (24h window expired), OR
 * - WhatsApp conversation with last successful user message >24h old
 */
export const useTemplateMessage = () => {
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);

  const dispatch = useDispatch();
  const { conversation } = useConversationDetail({ deskId, sessionId });

  const { messages } = useMessages(deskId, sessionId);
  const firstBatchOfMessages = messages?.pages?.[0]?.messages;

  const showTemplateMessage = useMemo(() => {
    const sorted = firstBatchOfMessages
      ?.slice()
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());

    const userMessage = sorted?.find(
      (message) => message.author_type === 'visitor' && !message.failed_at
    );

    const failedSystemMessage = sorted?.find(
      (message) =>
        message.author_type !== 'visitor' && message.failed_at && message.error_code === '131047'
    );

    // Calculate conversation state conditions
    const isExpired = !conversation?.state?.active;
    const isWhatsapp = conversation?.channel === 'whatsapp';
    const isLastMessageAfterOneDay = getDaysDifference(new Date(), userMessage?.created) >= 1;

    // Show template only if failed message exists AND is more recent than user message
    // If user sent a message after the failure, the 24h window has reopened
    const hasExpiredWindowError =
      !!failedSystemMessage &&
      (!userMessage ||
        new Date(failedSystemMessage.created).getTime() > new Date(userMessage.created).getTime());

    // Allow templates when conversation is active AND (131047 error occurs OR standard 24h window check)
    return !isExpired && (hasExpiredWindowError || (isWhatsapp && isLastMessageAfterOneDay));
  }, [firstBatchOfMessages, conversation?.state?.active, conversation?.channel]);

  const handleClick = useCallback(
    (mode: TemplateMessageMode = 'schedule') => {
      dispatch(
        pushModal(MODAL_TEMPLATE_MESSAGE, {
          desk_id: conversation?.desk_id,
          integration_id: conversation?.integration_id,
          mode,
          onPrimaryButtonClick: () => {
            dispatch(popModal());
          },
        })
      );
    },
    [conversation?.desk_id, conversation?.integration_id, dispatch]
  );

  return { showTemplateMessage, handleClick };
};
